// 詳細パネル v2 の「連絡状況」ブロック（最上段・最重要）。design/handoff-README.md §3 ④-2。
// 受信が最後なら「未返信」を大きく出し、アプリで送った／読んだ分は1クリックで記録して履歴の欠けを埋める。
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createGfTemplate,
  deleteGfTemplate,
  fetchGfIgMessages,
  fetchGfTemplates,
  postGfContacted,
  postGfDmSync,
  postGfIgBackfill,
  sendGfIgMessage,
  updateGfTemplate,
} from "../../../../api/giftingClient";
import { ConfirmModal } from "../../modals/ConfirmModal";
import {
  DM_MAX_CHARS,
  composeText,
  contactKindOf,
  fillTpl,
  fmtDateTime,
  fmtMD,
  fmtYmd,
  hasUnreplied,
  lastOutAt,
  linkedIgOf,
  parseServerTime,
  unfillTpl,
} from "../../../../lib/giftingRules";
import type {
  GfActionKey,
  GfContactKind,
  GfContactLog,
  GfDealDetail,
  GfEntry,
  GfIgMessage,
  GfSns,
  GfTemplate,
  Loadable,
} from "../../../../types/gifting";
import { errMsg } from "../../gfUtil";
import { copyText } from "../../ui/GfUi";
import { agoLabel, calDaysAgo } from "./dayLabel";

const THREAD_LIMIT = 6;
const TPL_NAME_MAX = 100;
const TPL_BODY_MAX = 3000;
const TPL_PLACEHOLDERS = "{名前} {商品} {フォームURL} {期限}";

/** 作成欄のテンプレ編集（その場で編集・いまの文面を保存・削除） */
interface TplEdit {
  /** edit=選択中のテンプレを直す／new=いまの文面から作る（上書きも選べる） */
  mode: "edit" | "new";
  id: string | null;
  name: string;
  body: string;
  /** 編集前の本文（作成欄が差し込み結果のままなら保存後に差し替える） */
  origBody: string;
  /** new のとき: 選択中のテンプレを上書きする */
  overwrite: boolean;
}

const KIND_LABEL: Record<GfContactKind, string> = {
  dm: "DM",
  pitch: "打診",
  nudge: "催促",
  reminder: "リマインド",
  reply_confirmed: "返信を確認",
  form_url: "フォームURL送付",
  auto_dm: "自動DM",
  note: "メモ",
};

/** スレッドに並べる1行 */
interface ThreadItem {
  key: string;
  at: string;
  /** in=受信／out=送信／note=手動記録（本文なし・中央の小さい行） */
  type: "in" | "out" | "note";
  text: string;
  /** アプリから送った記録（「· アプリ（手動記録）」） */
  manual: boolean;
  /** 送信元の補足（echo=Instagram アプリから送った／backfill=会話履歴から取込） */
  via: string;
  unreplied: boolean;
}

/** 送信元ラベル（ig-harness 0017 の origin） */
function originLabel(m: GfIgMessage): string {
  if (m.direction !== "out") return "";
  if (m.origin === "echo") return " · Instagram アプリから";
  if (m.origin === "backfill") return " · 履歴から取込";
  return "";
}

/** 作成欄のキー。"dm" = アクションに紐づかない自由文（テンプレから） */
type ComposeKey = GfActionKey | "dm";

function tms(s: string): number {
  return parseServerTime(s)?.getTime() ?? 0;
}

export interface ComposeRequest {
  key: GfActionKey;
  /** 同じ key でも開き直せるよう毎回増やす */
  n: number;
}

export function ContactBlock({
  entry,
  detail,
  creatorSns,
  contactChannel,
  name,
  campaignName,
  product,
  nowIso,
  request,
  readOnly,
  onSettled,
  changed,
  toast,
}: {
  entry: GfEntry;
  detail: GfDealDetail | null;
  creatorSns: GfSns[];
  contactChannel: string | null;
  name: string;
  campaignName: string;
  product: string;
  nowIso: string;
  /** 作成欄を開く要求（アクションボタン／URLの act から） */
  request: ComposeRequest | null;
  /** アーカイブ・中止 */
  readOnly: boolean;
  /** 送信／記録が完了した（親が nav.act を外す） */
  onSettled: () => void;
  changed: () => Promise<void>;
  toast: (m: string) => void;
}) {
  const sns = detail?.sns ?? creatorSns;
  const ig = linkedIgOf(sns);
  const fid = ig?.ig_follower_id ?? null;
  const linked = fid !== null;

  const [history, setHistory] = useState<Loadable<GfIgMessage[]> | null>(null);
  const [compose, setCompose] = useState<{ key: ComposeKey; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const threadRef = useRef<HTMLDivElement | null>(null);

  // テンプレ（作成欄を開いたときに1回だけ読む）
  const [templates, setTemplates] = useState<Loadable<GfTemplate[]> | null>(null);
  const [tplId, setTplId] = useState("");
  const [tplEdit, setTplEdit] = useState<TplEdit | null>(null);
  const [tplBusy, setTplBusy] = useState(false);
  const [tplErr, setTplErr] = useState<string | null>(null);
  const [tplDel, setTplDel] = useState(false);
  const loadTemplates = useCallback(async () => {
    setTemplates((t) => (t && t.status !== "error" ? t : { status: "loading" }));
    try {
      const list = await fetchGfTemplates();
      setTemplates({ status: "ok", data: [...list].sort((a, b) => a.sort_order - b.sort_order) });
    } catch (e) {
      setTemplates({ status: "error", message: errMsg(e) });
    }
  }, []);
  useEffect(() => {
    if (compose && !templates) void loadTemplates();
  }, [compose, templates, loadTemplates]);
  // 作成欄を閉じたらテンプレ編集も閉じる
  useEffect(() => {
    if (!compose) {
      setTplEdit(null);
      setTplErr(null);
    }
  }, [compose]);

  // 初回だけ Instagram の会話履歴を取り込む（アプリから送った過去分を埋める。ig-harness 側で1人1回に制限）
  const loadHistory = useCallback(async (backfill: "once" | "force" | undefined = "once") => {
    if (fid === null) return;
    try {
      const rows = await fetchGfIgMessages(fid, backfill ? { backfill } : {});
      setHistory({ status: "ok", data: rows });
    } catch (e) {
      setHistory({ status: "error", message: errMsg(e) });
    }
  }, [fid]);

  // 「Instagramから取り込む」: 会話履歴（直近20件）を取り直し → DM 状態の同期 → 一覧/パネルの再読込
  const backfillNow = async () => {
    if (fid === null) return;
    setBackfilling(true);
    try {
      const r = await postGfIgBackfill(fid);
      await postGfDmSync({ influencer_ids: [entry.influencer_id] }).catch(() => undefined);
      await Promise.all([changed(), loadHistory(undefined)]);
      toast(r.inserted > 0 ? `Instagram の履歴から ${r.inserted}件を取り込みました` : "新しく取り込むものはありませんでした");
    } catch (e) {
      toast(`履歴を取り込めませんでした: ${errMsg(e)}`);
    } finally {
      setBackfilling(false);
    }
  };

  useEffect(() => {
    if (fid === null) {
      setHistory(null);
      return;
    }
    setHistory({ status: "loading" });
    void loadHistory();
  }, [fid, loadHistory]);

  // パネルを開いた直後に1回だけ、この人の受信DMを同期（失敗・404 は無視）
  const synced = useRef(false);
  useEffect(() => {
    if (synced.current || (!entry.ig_linked && !linked)) return;
    synced.current = true;
    postGfDmSync({ influencer_ids: [entry.influencer_id] })
      .then((r) => {
        if (r && r.updated > 0) void changed().then(() => loadHistory(undefined));
      })
      .catch(() => undefined);
    // 開いた直後の1回だけ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.influencer_id, entry.ig_linked, linked]);

  // 作成欄を開く（詳細が読めてから＝定型文に URL・期限が入る）
  const lastReq = useRef(0);
  useEffect(() => {
    if (!request || request.n === lastReq.current || !detail) return;
    lastReq.current = request.n;
    setTplId("");
    setCompose({
      key: request.key,
      text: composeText(request.key, {
        name,
        campaignName,
        product,
        formUrl: detail.token_revoked === 1 ? null : detail.form_url,
        deadlineYmd: entry.post_deadline ? fmtYmd(entry.post_deadline) || entry.post_deadline.slice(0, 10) : null,
      }),
    });
  }, [request, detail, name, campaignName, product, entry.post_deadline]);

  // テンプレの差し込み（旧 DM パネルと同じ変数: {名前} {商品} {フォームURL} {期限}）
  const tplCtx = {
    name,
    product,
    formUrl: detail?.form_url ?? null,
    tokenRevoked: detail?.token_revoked === 1,
    deadline: entry.post_deadline ? fmtYmd(entry.post_deadline) || entry.post_deadline.slice(0, 10) : null,
  };
  const selectedTpl = templates?.status === "ok" ? templates.data.find((x) => x.id === tplId) : undefined;
  const pickTemplate = (id: string) => {
    setTplId(id);
    setTplEdit(null);
    const t = templates?.status === "ok" ? templates.data.find((x) => x.id === id) : undefined;
    if (!t) return;
    setCompose((c) => ({ key: c?.key ?? "dm", text: fillTpl(t.body, tplCtx) }));
  };
  /** 「テンプレを編集」: 選択中のテンプレの元文（変数のまま）を直す */
  const openTplEdit = () => {
    if (!selectedTpl) return;
    setTplErr(null);
    setTplEdit({ mode: "edit", id: selectedTpl.id, name: selectedTpl.name, body: selectedTpl.body, origBody: selectedTpl.body, overwrite: false });
  };
  /** 「文面をテンプレに保存」: いまの作成欄の文面を変数に戻してテンプレ化（選択中があれば上書きも選べる） */
  const openTplSave = () => {
    if (!compose) return;
    setTplErr(null);
    setTplEdit({
      mode: "new",
      id: null,
      name: selectedTpl?.name ?? "",
      body: unfillTpl(compose.text, tplCtx),
      origBody: "",
      overwrite: !!selectedTpl,
    });
  };
  const saveTpl = async () => {
    if (!tplEdit) return;
    const nm = tplEdit.name.trim();
    const body = tplEdit.body;
    if (!nm) return setTplErr("テンプレ名を入力してください");
    if (!body.trim()) return setTplErr("本文を入力してください");
    if (nm.length > TPL_NAME_MAX) return setTplErr(`テンプレ名は${TPL_NAME_MAX}字までです`);
    if (body.length > TPL_BODY_MAX) return setTplErr(`本文は${TPL_BODY_MAX}字までです`);
    const targetId = tplEdit.mode === "edit" ? tplEdit.id : tplEdit.overwrite ? selectedTpl?.id ?? null : null;
    setTplBusy(true);
    setTplErr(null);
    try {
      let id: string;
      if (targetId) {
        await updateGfTemplate(targetId, { name: nm, body });
        id = targetId;
        toast("テンプレを保存しました");
      } else {
        const max = templates?.status === "ok" ? Math.max(0, ...templates.data.map((t) => t.sort_order)) : 0;
        id = (await createGfTemplate({ name: nm, body, sort_order: max + 1 })).id;
        toast("テンプレを追加しました");
      }
      await loadTemplates();
      setTplId(id);
      // 編集前テンプレの差し込み結果がそのまま作成欄に残っているなら、新しい本文で差し替える（手で直した文面は触らない）
      if (tplEdit.mode === "edit") {
        setCompose((c) => (c && c.text === fillTpl(tplEdit.origBody, tplCtx) ? { ...c, text: fillTpl(body, tplCtx) } : c));
      }
      setTplEdit(null);
    } catch (e) {
      setTplErr(errMsg(e));
    } finally {
      setTplBusy(false);
    }
  };
  const deleteTpl = async () => {
    if (!tplEdit?.id) return;
    setTplBusy(true);
    try {
      await deleteGfTemplate(tplEdit.id);
      toast("テンプレを削除しました");
      setTplDel(false);
      setTplEdit(null);
      setTplId("");
      await loadTemplates();
    } catch (e) {
      setTplDel(false);
      setTplErr(errMsg(e));
    } finally {
      setTplBusy(false);
    }
  };
  /** 「文面を書く」: アクションに紐づかない自由文の作成欄（テンプレから選べる） */
  const openFreeCompose = () => {
    setTplId("");
    setCompose({ key: "dm", text: "" });
    window.setTimeout(() => document.querySelector(".gf-cb-compose")?.scrollIntoView({ behavior: "smooth", block: "nearest" }), 30);
  };

  const unreplied = hasUnreplied(entry);

  const items = useMemo<ThreadItem[]>(() => {
    const list: ThreadItem[] = [];
    if (history?.status === "ok") {
      for (const m of history.data) {
        list.push({ key: `m${m.id}`, at: m.created_at, type: m.direction, text: m.text, manual: false, via: originLabel(m), unreplied: false });
      }
    }
    // 手動記録: IG連携済は API 由来（IG履歴に載る）を除いてアプリ手動記録だけ。未連携は履歴が無いので全部。
    // アプリ手動記録の本文が IG 履歴（echo/取込）と同じなら二重に出さない
    const outTexts = new Set(list.filter((x) => x.type === "out").map((x) => `${x.text.trim()}@${x.at.slice(0, 10)}`));
    for (const l of detail?.contact_log ?? ([] as GfContactLog[])) {
      if (l.source === "api") continue;
      if (linked && l.source !== "app_manual") continue;
      if (l.direction === "in") {
        list.push({ key: `c${l.id}`, at: l.created_at, type: "in", text: l.body ?? KIND_LABEL[l.kind], manual: true, via: "", unreplied: false });
      } else if (l.body) {
        if (linked && outTexts.has(`${l.body.trim()}@${l.created_at.slice(0, 10)}`)) continue;
        list.push({ key: `c${l.id}`, at: l.created_at, type: "out", text: l.body, manual: l.source === "app_manual", via: "", unreplied: false });
      } else {
        const how = l.source === "app_manual" ? "アプリから" : "";
        list.push({ key: `c${l.id}`, at: l.created_at, type: "note", text: `${how}${KIND_LABEL[l.kind]}`, manual: true, via: "", unreplied: false });
      }
    }
    list.sort((a, b) => tms(a.at) - tms(b.at));
    const tail = list.slice(-THREAD_LIMIT);
    if (unreplied) {
      for (let i = tail.length - 1; i >= 0; i--) {
        if (tail[i].type === "in") {
          tail[i] = { ...tail[i], unreplied: true };
          break;
        }
      }
    }
    return tail;
  }, [history, detail?.contact_log, linked, unreplied]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [items]);

  const outAt = lastOutAt(entry);
  const outAgo = calDaysAgo(outAt, nowIso);
  const inAgo = calDaysAgo(entry.last_in_at, nowIso);

  const record = async (kind: NonNullable<Parameters<typeof postGfContacted>[1]>["kind"], via: "console" | "app", note: string | undefined, okMsg: string) => {
    setBusy(true);
    try {
      await postGfContacted(entry.id, { kind, via, channel: "ig_dm", ...(note ? { note } : {}) });
      await changed();
      if (linked) await loadHistory();
      toast(okMsg);
      return true;
    } catch (e) {
      toast(errMsg(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // 実送信は取り消せないので、宛先と本文を見直す確認を1枚はさむ（既存 DmPanel と同じ）
  const [confirmSend, setConfirmSend] = useState(false);
  const sendCompose = async () => {
    if (!compose) return;
    const text = compose.text.trim();
    if (!text || text.length > DM_MAX_CHARS) return;
    const kind = contactKindOf(compose.key, entry.stage);
    if (linked && fid !== null) {
      setBusy(true);
      try {
        await sendGfIgMessage(fid, { text, deal_id: entry.id, influencer_id: entry.influencer_id, kind });
      } catch (e) {
        toast(`送信できませんでした: ${errMsg(e)}`);
        setBusy(false);
        return;
      }
      try {
        await postGfContacted(entry.id, { kind, via: "console", channel: "ig_dm" });
        toast("DMを送信しました");
      } catch (e) {
        toast(`DMは送信しました。連絡の記録に失敗しました: ${errMsg(e)}`);
      }
      await changed();
      await loadHistory();
      setBusy(false);
      setCompose(null);
      onSettled();
    } else {
      const ok = await record(kind, "app", text, "アプリから送った記録を残しました");
      if (ok) {
        setCompose(null);
        onSettled();
      }
    }
  };

  const copyCompose = async () => {
    if (!compose) return;
    toast((await copyText(compose.text)) ? "文面をコピーしました" : "コピーできませんでした");
  };

  const stage2 = entry.stage === 2;
  const nudgeKind = entry.stage === 5 ? "reminder" : "nudge";
  const over = (compose?.text.trim().length ?? 0) > DM_MAX_CHARS;

  return (
    <section className={`gf-cb${unreplied ? " is-unreplied" : ""}`}>
      <div className="gf-cb-head">
        <span className="gf-cb-title">連絡状況</span>
        <span className={`gf-cb-ig${linked ? " is-on" : ""}`}>{linked ? "IG連携済" : "IG未連携"}</span>
        <span className="gf-cb-last">
          {outAt ? `最終送信 ${fmtMD(outAt)}（${agoLabel(outAgo)}）` : "未送信"}
        </span>
        {linked && (
          <button
            type="button"
            className="gf-link-btn gf-cb-backfill"
            disabled={backfilling}
            title="Instagram の会話履歴（直近20件）を取り込みます。アプリから送った DM が表示されていないときに"
            onClick={() => void backfillNow()}
          >
            {backfilling ? "取込中…" : "Instagramから取り込む"}
          </button>
        )}
      </div>

      {unreplied && (
        <div className="gf-cb-banner">
          <span className="gf-cb-banner-main">● 未返信</span>
          <span className="gf-cb-banner-sub">
            {agoLabel(inAgo)}に受信 · こちらの最後の送信は {outAt ? agoLabel(outAgo) : "なし"}
          </span>
        </div>
      )}

      {!linked && (
        <div className="gf-cb-nolink">
          DM履歴は取得できません（アプリで確認）
          <span className="gf-cb-nolink-sub">
            連絡手段: {contactChannel === "email" ? "メール" : contactChannel === "other" ? "その他" : "Instagram DM（未連携）"}
          </span>
        </div>
      )}
      {linked && history?.status === "loading" && <div className="gf-cb-nolink">DM履歴を読み込み中…</div>}
      {linked && history?.status === "error" && (
        <div className="gf-cb-nolink">
          DM履歴を取得できませんでした（{history.message}）
          <button type="button" className="gf-link-btn" onClick={() => void loadHistory()}>
            再読込
          </button>
        </div>
      )}

      {items.length > 0 && (
        <div className="gf-cb-thread" ref={threadRef}>
          {items.map((it) =>
            it.type === "note" ? (
              <div className="gf-cb-note" key={it.key}>
                記録：{it.text} · {fmtDateTime(it.at)}
              </div>
            ) : (
              <div className={`gf-cb-msg is-${it.type}${it.unreplied ? " is-unreplied" : ""}`} key={it.key}>
                <div className="gf-cb-bubble">{it.text}</div>
                <div className="gf-cb-meta">
                  {it.type === "in" ? "受信" : "送信"} {fmtDateTime(it.at)}
                  {it.via}
                  {it.manual && it.type === "out" ? " · アプリ（手動記録）" : ""}
                </div>
              </div>
            )
          )}
        </div>
      )}
      {linked && history?.status === "ok" && items.length === 0 && <div className="gf-cb-nolink">まだDMのやり取りはありません</div>}

      {compose && (
        <div className="gf-cb-compose">
          <div className="gf-cb-tpl">
            <select
              className="gf-select gf-cb-tpl-select"
              value={tplId}
              onChange={(e) => pickTemplate(e.target.value)}
              disabled={templates?.status !== "ok"}
              aria-label="テンプレートを差し込む"
            >
              <option value="">
                {templates?.status === "loading" || !templates
                  ? "テンプレを読み込み中…"
                  : templates.status === "error"
                    ? "テンプレを読み込めませんでした"
                    : templates.data.length
                      ? "— テンプレを選ぶと本文に差し込みます —"
                      : "テンプレがありません（下の「文面をテンプレに保存」で作れます）"}
              </option>
              {templates?.status === "ok" &&
                templates.data.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </select>
            {templates?.status === "error" && (
              <button type="button" className="gf-link-btn" onClick={() => void loadTemplates()}>
                再読込
              </button>
            )}
            {templates?.status === "ok" && !tplEdit && (
              <span className="gf-cb-tpl-links">
                {selectedTpl && (
                  <button type="button" className="gf-link-btn gf-cb-tpl-link" disabled={busy} onClick={openTplEdit}>
                    テンプレを編集
                  </button>
                )}
                <button type="button" className="gf-link-btn gf-cb-tpl-link" disabled={busy || !compose.text.trim()} onClick={openTplSave}>
                  文面をテンプレに保存
                </button>
              </span>
            )}
          </div>
          {tplEdit && (
            <div className="gf-cb-tpl-edit" role="group" aria-label="テンプレの編集">
              <div className="gf-cb-tpl-edit-title">
                {tplEdit.mode === "edit" ? "テンプレを編集" : "いまの文面をテンプレに保存"}
              </div>
              {tplEdit.mode === "new" && selectedTpl && (
                <div className="gf-cb-tpl-radio">
                  <label>
                    <input type="radio" name="gf-cb-tpl-mode" checked={tplEdit.overwrite} onChange={() => setTplEdit({ ...tplEdit, overwrite: true, name: selectedTpl.name })} />
                    「{selectedTpl.name}」を上書き
                  </label>
                  <label>
                    <input type="radio" name="gf-cb-tpl-mode" checked={!tplEdit.overwrite} onChange={() => setTplEdit({ ...tplEdit, overwrite: false, name: "" })} />
                    新しいテンプレとして追加
                  </label>
                </div>
              )}
              <input
                className="gf-input gf-cb-tpl-name"
                value={tplEdit.name}
                maxLength={TPL_NAME_MAX}
                placeholder="テンプレ名（例）初回のご挨拶"
                aria-label="テンプレ名"
                onChange={(e) => setTplEdit({ ...tplEdit, name: e.target.value })}
              />
              <textarea
                className="gf-textarea gf-cb-ta"
                rows={5}
                value={tplEdit.body}
                aria-label="テンプレ本文"
                onChange={(e) => setTplEdit({ ...tplEdit, body: e.target.value })}
              />
              <div className={`gf-cb-tpl-hint${tplEdit.body.length > TPL_BODY_MAX ? " is-over" : ""}`}>
                {tplEdit.body.length}字 · <span className="gf-mono">{TPL_PLACEHOLDERS}</span> は差し込み時に置き換わります
              </div>
              {tplErr && <div className="gf-cb-tpl-err">{tplErr}</div>}
              <div className="gf-cb-tpl-actions">
                <button type="button" className="gf-cb-send" disabled={tplBusy} onClick={() => void saveTpl()}>
                  {tplBusy ? "保存中…" : tplEdit.mode === "edit" || tplEdit.overwrite ? "テンプレを保存" : "テンプレを追加"}
                </button>
                <button type="button" className="gf-cb-cancel" disabled={tplBusy} onClick={() => setTplEdit(null)}>
                  取消
                </button>
                {tplEdit.mode === "edit" && (
                  <button type="button" className="gf-link-btn is-danger gf-cb-tpl-link" disabled={tplBusy} onClick={() => setTplDel(true)}>
                    このテンプレを削除
                  </button>
                )}
              </div>
            </div>
          )}
          <textarea
            className="gf-textarea gf-cb-ta"
            rows={4}
            value={compose.text}
            onChange={(e) => setCompose({ ...compose, text: e.target.value })}
            aria-label="送る文面"
          />
          <div className={`gf-cb-count${over ? " is-over" : ""}`}>
            {compose.text.trim().length} / {DM_MAX_CHARS}字
            {linked ? "　送信すると相手にすぐ届きます（取り消せません）" : "　アプリから送ったら「記録」を押してください"}
          </div>
          <div className="gf-cb-compose-actions">
            {linked ? (
              <button type="button" className="gf-cb-send" disabled={busy || !compose.text.trim() || over} onClick={() => setConfirmSend(true)}>
                {busy ? "送信中…" : "DMを送信"}
              </button>
            ) : (
              <>
                <button type="button" className="gf-cb-copy" disabled={!compose.text.trim()} onClick={() => void copyCompose()}>
                  文面をコピー
                </button>
                <button type="button" className="gf-cb-send" disabled={busy || !compose.text.trim() || over} onClick={() => void sendCompose()}>
                  アプリから送った を記録
                </button>
              </>
            )}
            <button type="button" className="gf-cb-cancel" disabled={busy} onClick={() => setCompose(null)}>
              キャンセル
            </button>
          </div>
        </div>
      )}
      <ConfirmModal
        open={confirmSend && !!compose}
        title="Instagram DM を送信します"
        message="相手に実際に届き、取り消せません。宛先と本文を確認してください。"
        confirmLabel={busy ? "送信中…" : "送信する"}
        busy={busy}
        onConfirm={() => {
          setConfirmSend(false);
          void sendCompose();
        }}
        onClose={() => !busy && setConfirmSend(false)}
      >
        {compose && <div className="gf-boxed gf-cb-confirm-body">{compose.text.trim()}</div>}
      </ConfirmModal>
      <ConfirmModal
        open={tplDel && !!tplEdit?.id}
        title="テンプレを削除"
        confirmLabel={tplBusy ? "削除中…" : "削除する"}
        danger
        busy={tplBusy}
        onConfirm={() => void deleteTpl()}
        onClose={() => !tplBusy && setTplDel(false)}
      >
        <p>「{tplEdit?.name}」を削除しますか。元に戻せません。</p>
      </ConfirmModal>

      {!readOnly && !compose && (
        <div className="gf-cb-write">
          <button type="button" className="gf-cb-mbtn is-write" disabled={busy} onClick={openFreeCompose}>
            {linked ? "DMを書く（テンプレから）" : "文面を書く（テンプレから）"}
          </button>
        </div>
      )}

      {!readOnly && (
        <div className="gf-cb-manual">
          <div className="gf-cb-manual-cap">アプリで送った／読んだ分は1クリックで記録</div>
          <div className="gf-cb-manual-row">
            <button
              type="button"
              className="gf-cb-mbtn"
              disabled={busy}
              onClick={() => void record("pitch", "app", undefined, entry.stage === 1 ? "打診を記録しました（返信待ちへ）" : "打診を記録しました")}
            >
              アプリから打診した
            </button>
            <button
              type="button"
              className={`gf-cb-mbtn${unreplied ? " is-blue" : ""}`}
              disabled={busy}
              onClick={() =>
                void record("reply_confirmed", "app", undefined, stage2 ? "返信を確認しました（条件合意へ）" : "返信を確認しました")
              }
            >
              返信を確認した{stage2 ? "（返信待ち→条件合意へ）" : ""}
            </button>
            <button
              type="button"
              className="gf-cb-mbtn"
              disabled={busy}
              onClick={() => void record(nudgeKind, "app", undefined, entry.stage === 5 ? "リマインドを記録しました" : "催促を記録しました")}
            >
              アプリから催促した
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
